import React, { FC, Fragment, useRef, useState } from 'react';
import { Grid, TextField, Typography, Theme, FormControlLabel, Card, CardHeader, CardContent, Checkbox, Button, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { grey } from '@material-ui/core/colors';
import { ClientBody } from 'typings/body/ClientBody';
import { ServiceAddressBody } from 'typings/body/ServiceAddressBody';
import { ONEMAP_BASE_URL } from 'constants/url';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { combineAddress } from 'utils';

import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

interface Props {
  client: ClientBody;
  setClient: React.Dispatch<React.SetStateAction<ClientBody>>;
  tempPostalValue: Select[];
  setTempPostalValue: React.Dispatch<React.SetStateAction<Select[]>>;
  locationErrorMessage: any[];
  setLocationErrorMessage: React.Dispatch<React.SetStateAction<any[]>>;
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    margin: 'auto',
    marginBottom: theme.spacing(2),
    width: '100%'
  },
  redColor: {
    color: theme.palette.error.main
  },
  textField: {
    marginBottom: theme.spacing(1),
    marginTop: 0
  }
}));

const LocationForm: FC<Props> = props => {
  const classes = useStyles();
  const { client, setClient, tempPostalValue, setTempPostalValue, locationErrorMessage, setLocationErrorMessage } = props;

  const { billingAddress, billingFloorNo, billingUnitNo, billingPostal, ServiceAddresses } = client;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSameAddress, setIsSameAddress] = useState<boolean>(billingPostal === ServiceAddresses[0]?.postalCode);

  const [addressOptions, setAddressOptions] = useState<Select[]>([]);
  const latestPostalQuery = useRef<string>('');

  const getAddress = async (postalCode: string) => {
    latestPostalQuery.current = postalCode;
    try {
      setIsLoading(true);
      setAddressOptions([]);
      try {
        const response = await fetch(`${ONEMAP_BASE_URL}/search/${encodeURIComponent(postalCode)}`);
        const { success, data } = await response.json();

        // ignore responses from an older keystroke
        if (latestPostalQuery.current !== postalCode) {
          return;
        }

        // Autocomplete crashes the whole page if options is not an array or an option has no string name
        if (success && Array.isArray(data)) {
          setAddressOptions(data.filter((option: any) => option && typeof option.name === 'string'));
        } else {
          setAddressOptions([]);
        }
      } catch (error) {
        console.log(error);
        setAddressOptions([]);
      }

      // const getQueryParams = () => {
      //   const params = new URLSearchParams();
      //   params.append('returnGeom', 'N');
      //   params.append('getAddrDetails', 'Y');
      //   params.append('searchVal', postalCode);
      //   return params.toString();
      // };

      // const url = `${ONEMAP_BASE_URL}?${getQueryParams()}`;
      // const response = await fetch(url);
      // const { results } = await response.json();
      // let fetchAddress: Select[] = [];

      // if (results && results.length > 0) {
      //   // eslint-disable-next-line array-callback-return
      //   results.map((value: any) => {
      //     if (!isNaN(value['POSTAL'])) {
      //       return fetchAddress.push({
      //         id: Number(value['POSTAL']),
      //         name: value['BUILDING'].includes('NIL')
      //           ? `${value['BLK_NO']} ${value['ROAD_NAME']}, ${value['POSTAL']}`
      //           : `${value['BUILDING']}, ${value['BLK_NO']} ${value['ROAD_NAME']}, ${value['POSTAL']}`,
      //         value: value['BUILDING'].includes('NIL')
      //           ? `${value['BLK_NO']} ${value['ROAD_NAME']}`
      //           : `${value['BUILDING']}, ${value['BLK_NO']} ${value['ROAD_NAME']}`,
      //         tempValue: value['POSTAL']
      //       });
      //     }
      //   });
      // }

      if (latestPostalQuery.current === postalCode) {
        setIsLoading(false);
      }
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    }
  };

  // Never mutate ServiceAddresses in place: it may still be the shared dummyClientBody object,
  // and the functional update keeps the latest state even after awaiting getAddress
  const updateServiceAddress = (index: number, changes: Partial<ServiceAddressBody>, clientChanges: Partial<ClientBody> = {}) => {
    setClient(prev => ({
      ...prev,
      ...clientChanges,
      ServiceAddresses: prev.ServiceAddresses.map((address, i) => (i === index ? { ...address, ...changes } : address))
    }));
  };

  const clearLocationError = (indexes: number[]) => {
    setLocationErrorMessage(prev => {
      const next = prev.map(error => ({ ...error }));
      indexes.forEach(i => {
        next[i] = { postalCode: '', address: '' };
      });
      return next;
    });
  };

  const setTempPostalValueAt = (i: number, newValue: Select) => {
    setTempPostalValue(prev => {
      const next = [...prev];
      next[i] = newValue;
      return next;
    });
  };

  const handleRemoveSubServiceAddress = (i: number): React.MouseEventHandler => () => {
    setClient(prev => ({ ...prev, ServiceAddresses: prev.ServiceAddresses.filter((_, index) => index !== i) }));
    setTempPostalValue(prev => prev.filter((_, index) => index !== i + 1));
    setLocationErrorMessage(prev => prev.filter((_, index) => index !== i + 1));
  };

  const handleAddSubServiceAddress = () => {
    const firstAddress = ServiceAddresses[0];
    setClient(prev => ({
      ...prev,
      ServiceAddresses: [
        ...prev.ServiceAddresses,
        {
          id: 0,
          contactPerson: firstAddress.contactPerson,
          countryCode: firstAddress.countryCode,
          contactNumber: firstAddress.contactNumber,
          secondaryContactPerson: firstAddress.secondaryContactPerson,
          secondaryContactNumber: firstAddress.secondaryContactNumber,
          country: firstAddress.country,
          address: '',
          floorNo: '',
          unitNo: '',
          postalCode: '',
          clientId: firstAddress.clientId
        }
      ]
    }));
    setTempPostalValue(prev => [...prev, { id: 0, name: '' }]);
    setLocationErrorMessage(prev => [...prev, { postalCode: '', address: '' }]);
  };

  const handleClearServicePostal = (index: number) => {
    updateServiceAddress(index, { postalCode: '', address: '' });
  };

  const handleFreeTextServicePostal = async (value: string, index: number) => {
    if (value && isNaN(Number(value))) {
      return;
    }

    const postalCode = value || '';
    updateServiceAddress(index, { postalCode }, isSameAddress && index === 0 ? { billingPostal: postalCode } : {});

    if (postalCode.length > 5) {
      await getAddress(postalCode);
    }
  };

  const handleChangeServicePostal = (value: any, index: number) => {
    if (value) {
      const { floorNo, unitNo } = ServiceAddresses[index];
      const postalCode = String(value.tempValue);
      const fullAddress = combineAddress(value.value, floorNo || '', unitNo || '', postalCode);

      if (isSameAddress && index === 0) {
        updateServiceAddress(index, { address: fullAddress, postalCode }, { billingPostal: postalCode, billingAddress: fullAddress });
        clearLocationError([0, index + 1]);
      } else {
        updateServiceAddress(index, { address: fullAddress, postalCode });
        clearLocationError([index + 1]);
      }

      setTempPostalValueAt(index + 1, { id: value.id, name: value.value });
    }
  };

  const handleChangeServiceFloorNo = (value: string, index: number) => {
    const { unitNo, postalCode } = ServiceAddresses[index];
    const fullAddress = combineAddress(tempPostalValue[index + 1]?.name || '', value, unitNo || '', postalCode);
    if (isSameAddress && index === 0) {
      updateServiceAddress(index, { floorNo: value, address: fullAddress }, { billingFloorNo: value, billingAddress: fullAddress });
    } else {
      updateServiceAddress(index, { floorNo: value, address: fullAddress });
    }
  };

  const handleChangeServiceUnitNo = (value: string, index: number) => {
    const { floorNo, postalCode } = ServiceAddresses[index];
    const fullAddress = combineAddress(tempPostalValue[index + 1]?.name || '', floorNo || '', value || '', postalCode);
    if (isSameAddress && index === 0) {
      updateServiceAddress(index, { unitNo: value, address: fullAddress }, { billingUnitNo: value, billingAddress: fullAddress });
    } else {
      updateServiceAddress(index, { unitNo: value, address: fullAddress });
    }
  };

  const handleChangeServiceAddress = (value: string, index: number) => {
    if (isSameAddress && index === 0) {
      updateServiceAddress(index, { address: value }, { billingAddress: value });
    } else {
      updateServiceAddress(index, { address: value });
    }
  };

  const handleOnBlur = (field: string, value: string, index: number) => {
    const message = !value ? (field === 'postalCode' ? 'Please enter postal code' : 'Please enter address') : '';
    setLocationErrorMessage(prev => {
      const next = prev.map(error => ({ ...error }));
      next[index] = { postalCode: '', address: '', ...next[index], [field]: message };
      return next;
    });
  };

  const renderServiceAddress = () => {
    return (
      ServiceAddresses &&
      ServiceAddresses.length > 0 &&
      ServiceAddresses.map((value, index) => {
        return (
          <Fragment key={`service-address-${index}`}>
            <Grid container>
              <Grid item xs={12} md={4}>
                <Typography variant='h6'>
                  Service Address {index + 1}
                  {index === 0 && <span className={classes.redColor}>*</span>}
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      id='postalCode-0'
                      options={addressOptions}
                      getOptionLabel={option => option?.name ?? ''}
                      value={tempPostalValue[index + 1] ?? null}
                      inputValue={value.postalCode || ''}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') {
                          handleFreeTextServicePostal(value, index);
                        }
                        if (reason === 'clear') {
                          handleClearServicePostal(index);
                        }
                      }}
                      onChange={(_, value) => {
                        handleChangeServicePostal(value, index);
                      }}
                      autoHighlight
                      // freeSolo
                      loading={isLoading}
                      loadingText='Searching…'
                      noOptionsText='No options'
                      renderInput={params => (
                        <TextField
                          {...params}
                          required
                          margin='dense'
                          className={classes.textField}
                          id='postalCode'
                          label='Postal Code'
                          variant='outlined'
                          error={!!locationErrorMessage[index + 1]?.postalCode}
                          helperText={locationErrorMessage[index + 1]?.postalCode}
                          onBlur={event => handleOnBlur('postalCode', event.target.value, index + 1)}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      variant='outlined'
                      margin='dense'
                      fullWidth
                      id='floor'
                      label='Floor No'
                      value={value.floorNo}
                      onChange={event => handleChangeServiceFloorNo(event.target.value, index)}
                      autoComplete='off'
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      variant='outlined'
                      margin='dense'
                      fullWidth
                      id='unit'
                      label='Unit No'
                      value={value.unitNo}
                      onChange={event => handleChangeServiceUnitNo(event.target.value, index)}
                      autoComplete='off'
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant='outlined'
                      margin='dense'
                      required
                      fullWidth
                      multiline={true}
                      rows={3}
                      id='address'
                      label='Address'
                      value={value.address}
                      error={!!locationErrorMessage[index + 1]?.address}
                      helperText={locationErrorMessage[index + 1]?.address}
                      autoComplete='off'
                      className={classes.textField}
                      onChange={event => handleChangeServiceAddress(event.target.value, index)}
                      onBlur={event => handleOnBlur('address', event.target.value, index + 1)}
                    />
                  </Grid>
                </Grid>
                <Grid container style={{ marginTop: 8 }}>
                  {index !== 0 && (
                    <Grid item xs={6}>
                      <Button onClick={handleRemoveSubServiceAddress(index)} className={classes.redColor} startIcon={<DeleteIcon />}>
                        Remove Address
                      </Button>
                    </Grid>
                  )}
                  {index === ServiceAddresses.length - 1 && (
                    <Grid item container justify='flex-end' xs={index !== 0 ? 6 : 12}>
                      <Button color='primary' startIcon={<AddIcon />} onClick={handleAddSubServiceAddress}>
                        More Address
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {index !== ServiceAddresses.length - 1 && <Divider style={{ marginTop: 16, marginBottom: 16 }} />}
          </Fragment>
        );
      })
    );
  };

  const hanldeCheckBoxChange = () => {
    setIsSameAddress(!isSameAddress);
    const { postalCode, floorNo, unitNo, address } = ServiceAddresses[0];
    setClient({
      ...client,
      billingPostal: !isSameAddress ? postalCode : '',
      billingFloorNo: !isSameAddress ? floorNo : '',
      billingUnitNo: !isSameAddress ? unitNo : '',
      billingAddress: !isSameAddress ? address : ''
    });
    clearLocationError([0]);
  };

  const handleFreeTextBillingPostal = async (value: string) => {
    if (value) {
      if (!isNaN(Number(value))) {
        setClient(prev => ({ ...prev, billingPostal: value }));
        if (value.length > 5) {
          await getAddress(value);
        }
      }
    } else {
      setClient(prev => ({ ...prev, billingPostal: '' }));
      setAddressOptions([]);
    }
  };

  const handleChangeBillingPostal = (value: any) => {
    if (value) {
      const fullAddress = combineAddress(value.value, billingFloorNo, billingUnitNo, value.tempValue);
      setTempPostalValueAt(0, { id: value.id, name: value.value });
      setClient({ ...client, billingPostal: String(value.tempValue), billingAddress: fullAddress });
      clearLocationError([0]);
    } else {
      setTempPostalValueAt(0, { id: 0, name: '' });
      setClient({ ...client, billingPostal: '', billingAddress: '' });
    }
  };

  const handleChangeBillingFloor = (value: string) => {
    const fullAddress = combineAddress(tempPostalValue[0]?.name || '', value, billingUnitNo, billingPostal);
    setClient({ ...client, billingFloorNo: value, billingAddress: fullAddress });
  };

  const handleChangeBillingUnit = (value: string) => {
    const fullAddress = combineAddress(tempPostalValue[0]?.name || '', billingFloorNo, value, billingPostal);
    setClient({ ...client, billingUnitNo: value, billingAddress: fullAddress });
  };

  const renderBillingAddress = () => {
    return (
      <>
        <Grid item xs={12} md={4}>
          <Typography variant='h6'>
            Billing Address<span className={classes.redColor}>*</span>
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={isSameAddress} color='primary' onChange={hanldeCheckBoxChange} />}
            label={
              <Typography variant='caption' color='textSecondary'>
                Mark same as with Service Address 1
              </Typography>
            }
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                id='postalCode-0'
                options={addressOptions}
                getOptionLabel={option => option?.name ?? ''}
                value={tempPostalValue[0] ?? null}
                inputValue={billingPostal || ''}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    handleFreeTextBillingPostal(value);
                  }
                }}
                onChange={(_, value) => {
                  handleChangeBillingPostal(value);
                }}
                autoHighlight
                // freeSolo
                loading={isLoading}
                loadingText='Searching…'
                noOptionsText='No options'
                disabled={isSameAddress}
                renderInput={params => (
                  <TextField
                    {...params}
                    required
                    margin='dense'
                    className={classes.textField}
                    id='postalCode'
                    label='Postal Code'
                    variant='outlined'
                    error={!!locationErrorMessage[0]?.postalCode}
                    helperText={locationErrorMessage[0]?.postalCode}
                    onBlur={event => handleOnBlur('postalCode', event.target.value, 0)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                variant='outlined'
                margin='dense'
                fullWidth
                disabled={isSameAddress}
                id='floor'
                label='Floor No'
                value={billingFloorNo}
                onChange={event => handleChangeBillingFloor(event.target.value)}
                autoComplete='off'
                className={classes.textField}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                variant='outlined'
                margin='dense'
                fullWidth
                disabled={isSameAddress}
                id='unit'
                label='Unit No'
                value={billingUnitNo}
                onChange={event => handleChangeBillingUnit(event.target.value)}
                autoComplete='off'
                className={classes.textField}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant='outlined'
                margin='dense'
                required
                fullWidth
                multiline={true}
                rows={3}
                disabled={isSameAddress}
                id='address'
                label='Address'
                value={billingAddress}
                error={!!locationErrorMessage[0]?.address}
                helperText={locationErrorMessage[0]?.address}
                onChange={event => setClient({ ...client, billingAddress: event.target.value })}
                autoComplete='off'
                className={classes.textField}
                onBlur={event => handleOnBlur('address', event.target.value, 0)}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      <Card variant='outlined' className={classes.card}>
        <CardHeader title={<Typography variant='subtitle2'>Service Address</Typography>} style={{ backgroundColor: grey[200], height: 35 }} />
        <CardContent>{renderServiceAddress()}</CardContent>
      </Card>
      <Card variant='outlined' className={classes.card}>
        <CardHeader title={<Typography variant='subtitle2'>Billing Address</Typography>} style={{ backgroundColor: grey[200], height: 35 }} />
        <CardContent>
          <Grid container>{renderBillingAddress()}</Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default LocationForm;
